<section class="search-records">
  @if (isLoading()) {
  <div class="state-panel" role="status" aria-live="polite">
    <app-progress-spinner [diameter]="56" />
    <h2 class="state-panel__title">Loading request…</h2>
    <p class="state-panel__message">Hang tight while we fetch the submitted request details.</p>
  </div>
  } @else if (loadError()) {
  <div class="state-panel" role="alert">
    <h1 class="state-panel__title">{{ loadErrorTitle() }}</h1>
    <p class="state-panel__message">{{ loadError() }}</p>
    <a class="primary-button fw-600" routerLink="/requests">
      <app-icon
        name="chevron_left"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Back to Requests</span>
    </a>
  </div>
  } @else {
  <nav class="breadcrumb flex align-center gap-8 fs-12 mb-15" aria-label="Breadcrumb">
    <a class="breadcrumb__link fw-600" routerLink="/requests">Requests</a>
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <a class="breadcrumb__link fw-600" [routerLink]="detailLink()">{{ breadcrumbLabel() }}</a>
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <span aria-current="page">Search Patient Records</span>
  </nav>

  <header class="page-header">
    <h1 class="page-title fs-22 fw-700 m-0">Search Patient Records</h1>
    <p class="page-subtitle">
      Fields are pre-filled from the submitted request. Edit any field and re-run the search if
      needed.
    </p>
  </header>

  <section class="info-card" aria-labelledby="search-parameters-title">
    <div class="info-card__header flex align-center gap-8">
      <app-icon
        class="info-card__icon"
        name="manage_search"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <h2 id="search-parameters-title" class="info-card__title">Search Parameters</h2>
      <span class="info-card__meta fs-12 flex align-center gap-4">
        <app-icon
          name="info"
          size="xs"
          color="custom"
          customColor="currentColor"
          role="presentation"
        ></app-icon>
        <span>Pre-filled from submitted request · at least two fields are required</span>
      </span>
    </div>

    <div class="info-card__body">
      <form (submit)="onSearchSubmit($event)">
        <div class="fields-grid">
          <div class="field">
            <label class="field__label fs-12 fw-600" for="search-last-name">Last Name</label>
            <input
              id="search-last-name"
              type="text"
              class="field__input"
              [class.field__input--prefilled]="isPrefilled('lastName', lastNameValue())"
              [class.field__input--invalid]="lastNameInvalid()"
              [value]="lastNameValue()"
              (input)="lastNameValue.set($any($event.target).value)"
              autocomplete="family-name"
              [attr.maxlength]="maxNameLength"
              [attr.aria-invalid]="lastNameInvalid()"
              [attr.aria-describedby]="lastNameInvalid() ? 'search-last-name-error' : null"
            />
            @if (lastNameInvalid()) {
            <p id="search-last-name-error" class="field__error fs-12" role="alert">
              Enter at least {{ minFieldLength }} characters for the last name.
            </p>
            }
          </div>

          <div class="field">
            <label class="field__label fs-12 fw-600" for="search-first-name">First Name</label>
            <input
              id="search-first-name"
              type="text"
              class="field__input"
              [class.field__input--prefilled]="isPrefilled('firstName', firstNameValue())"
              [class.field__input--invalid]="firstNameInvalid()"
              [value]="firstNameValue()"
              (input)="firstNameValue.set($any($event.target).value)"
              autocomplete="given-name"
              [attr.maxlength]="maxNameLength"
              [attr.aria-invalid]="firstNameInvalid()"
              [attr.aria-describedby]="firstNameInvalid() ? 'search-first-name-error' : null"
            />
            @if (firstNameInvalid()) {
            <p id="search-first-name-error" class="field__error fs-12" role="alert">
              Enter at least {{ minFieldLength }} characters for the first name.
            </p>
            }
          </div>

          <div class="field">
            <label class="field__label fs-12 fw-600" for="search-dob">Date of Birth</label>
            <input
              id="search-dob"
              type="text"
              class="field__input"
              [class.field__input--prefilled]="isPrefilled('dateOfBirth', dateOfBirthValue())"
              [class.field__input--invalid]="dateOfBirthInvalid()"
              [value]="dateOfBirthValue()"
              (input)="dateOfBirthValue.set($any($event.target).value)"
              placeholder="MM/DD/YYYY"
              inputmode="numeric"
              [attr.aria-invalid]="dateOfBirthInvalid()"
              [attr.aria-describedby]="dateOfBirthInvalid() ? 'search-dob-error' : null"
            />
            @if (dateOfBirthInvalid()) {
            <p id="search-dob-error" class="field__error fs-12" role="alert">
              Enter the date of birth as MM/DD/YYYY.
            </p>
            }
          </div>

          <div class="field">
            <label class="field__label fs-12 fw-600" for="search-ssn">SSN (last 5)</label>
            <input
              id="search-ssn"
              type="text"
              class="field__input"
              [class.field__input--prefilled]="isPrefilled('ssn', ssnValue())"
              [class.field__input--invalid]="ssnInvalid()"
              [value]="ssnValue()"
              (input)="ssnValue.set($any($event.target).value)"
              placeholder="Enter last 5 digits..."
              [attr.maxlength]="maxSsnLength"
              inputmode="numeric"
              autocomplete="off"
              [attr.aria-invalid]="ssnInvalid()"
              [attr.aria-describedby]="ssnInvalid() ? 'search-ssn-error' : null"
            />
            @if (ssnInvalid()) {
            <p id="search-ssn-error" class="field__error fs-12" role="alert">
              Enter digits only for the last 5 of the SSN.
            </p>
            }
          </div>

          <div class="field">
            <label class="field__label fs-12 fw-600" for="search-mrn">MRN</label>
            <input
              id="search-mrn"
              type="text"
              class="field__input"
              [class.field__input--prefilled]="isPrefilled('mrn', mrnValue())"
              [class.field__input--invalid]="mrnInvalid()"
              [value]="mrnValue()"
              (input)="mrnValue.set($any($event.target).value)"
              placeholder="Optional"
              [attr.maxlength]="maxMrnLength"
              [attr.aria-invalid]="mrnInvalid()"
              [attr.aria-describedby]="mrnInvalid() ? 'search-mrn-error' : null"
            />
            @if (mrnInvalid()) {
            <p id="search-mrn-error" class="field__error fs-12" role="alert">
              Enter at least {{ minFieldLength }} characters for the MRN.
            </p>
            }
          </div>
        </div>

        <div class="form-actions flex justify-end gap-8">
          <button
            type="button"
            class="secondary-button form-actions__button fw-600"
            (click)="clearForm()"
          >
            <app-icon
              name="close"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span>Clear</span>
          </button>
          <button
            type="submit"
            class="primary-button form-actions__button fw-600"
            [disabled]="!canRunSearch() || isSearching()"
          >
            <app-icon
              name="manage_search"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span>{{ isSearching() ? 'Searching…' : 'Search' }}</span>
          </button>
        </div>
      </form>
    </div>
  </section>

  <section class="info-card" aria-labelledby="search-results-title">
    <div class="info-card__header flex align-center gap-8">
      <h2 id="search-results-title" class="info-card__title info-card__title--grow">
        Search Results
      </h2>
      @if (!isSearching()) {
      <span class="info-card__meta fs-12">{{ resultSummary() }}</span>
      }
    </div>

    @if (searchError()) {
    <p class="search-error flex align-center gap-8 fs-12" role="alert">
      <app-icon
        name="info"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>{{ searchError() }}</span>
    </p>
    }

    <!-- The search returns a fixed page with no paging UI, so say so when the
         server matched more — otherwise staff could deny a request whose real
         match sat past the cut-off. -->
    @if (!isSearching() && isTruncated()) {
    <p class="search-truncated flex align-center gap-8 fs-12" role="status">
      <app-icon
        name="info"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span
        >More records matched than are shown. Narrow the criteria to see the rest before deciding
        this request has no match.</span
      >
    </p>
    } @if (isSearching()) {
    <div class="results-loading flex align-center gap-10" role="status" aria-live="polite">
      <app-progress-spinner [diameter]="24" />
      <span class="results-loading__text">Searching EHR…</span>
    </div>
    } @else if (results().length > 0) {
    <div class="results-table" role="table" aria-label="Patient record search results">
      <div class="results-table__header" role="row">
        <span role="columnheader">Patient Name</span>
        <span role="columnheader">DOB</span>
        <span role="columnheader">MRN</span>
        <span role="columnheader">Primary Care</span>
        <span role="columnheader">Phone</span>
        <span role="columnheader">Match</span>
        <span role="columnheader" class="sr-only">Actions</span>
      </div>
      @for (result of results(); track result.patientId) {
      <!-- The leading row is tinted *and* labelled: colour alone would carry the recommendation for
           sighted staff only, and this is the row the screen is nudging someone to link. -->
      <div
        class="results-table__row"
        role="row"
        [class.results-table__row--leading]="isLeadingMatch(result)"
      >
        <span class="results-table__name-cell" role="cell">
          <span class="results-table__avatar" aria-hidden="true">
            {{ getResultInitials(result) }}
          </span>
          <span class="flex flex-col">
            <span class="results-table__name fw-600">{{ result.displayName }}</span>
            <span class="results-table__address">{{ getResultAddress(result) }}</span>
          </span>
        </span>
        <span class="results-table__cell" role="cell" data-label="DOB"
          >{{ getResultDateOfBirth(result) }}</span
        >
        <span class="results-table__cell" role="cell" data-label="MRN">
          {{ getResultMedicalRecordNumber(result) }}
        </span>
        <span class="results-table__cell" role="cell" data-label="Primary Care">
          {{ getResultPrimaryCareProvider(result) }}
        </span>
        <span class="results-table__cell" role="cell" data-label="Phone"
          >{{ getResultPhoneNumber(result) }}</span
        >
        <span class="results-table__cell" role="cell" data-label="Match">
          @let badge = getResultConfidenceBadge(result);
          <span class="badge badge--compact {{ badge.class }}">{{ badge.label }}</span>
          @if (isLeadingMatch(result)) {
          <span class="results-table__leading-flag">{{ leadingMatchLabel() }}</span>
          }
        </span>
        <span role="cell">
          <button
            type="button"
            class="primary-button results-table__select fw-600"
            (click)="selectAndReview(result)"
            [attr.aria-label]="'Select and review record for ' + result.displayName"
          >
            Select &amp; Review
          </button>
        </span>
      </div>
      }
    </div>
    } @else {
    <div class="empty-state" role="status" aria-live="polite">
      <h3 class="empty-state__title">No matching records found</h3>
      <p class="empty-state__message">{{ emptyStateMessage() }}</p>
    </div>
    }
  </section>
  }
</section>
